import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { automaticImportApi } from "libs/api";
import type { ExtendedScrapingErrorResponse } from "libs/codegen/property-onboarding";
import { useState } from "react";
import { useNavigate, useParams } from "router";
import { usePropertySelectionViewModelValue } from "../propertySelectionViewModelAtom.ts";
import { PropertyType } from "./constants";
import { externalPropertyTypeMapper } from "./externalPropertyTypeMapper";
import { scrapingErrorResponseMapper } from "./scrapingErrorResponseMapper.ts";
import { useSetURLInput } from "./urlInputAtom";

export const useURLValidationHandler = () => {
    const [input, setInput] = useState<string>("");

    const { shouldImportBcomWithNewFlow } = usePropertySelectionViewModelValue();
    const [error, setError] = useState<ExtendedScrapingErrorResponse["errorType"] | null>(null);

    const setUrlInput = useSetURLInput();
    const { lang } = useParams("/:lang/list-my-property/get-started/automatic");
    const navigate = useNavigate();

    const { mutate, isLoading } = useMutation({
        mutationFn: (url: string) => {
            setError(null);

            if (url.trim().length === 0)
                throw {
                    response: {
                        data: {
                            errorType: scrapingErrorResponseMapper.EmptyInput,
                        },
                    },
                };

            return automaticImportApi.validateUrl(lang, url);
        },
        onSuccess: ({ data }) => {
            if (data.externalPropertyType === externalPropertyTypeMapper.Airbnb || shouldImportBcomWithNewFlow) {
                setUrlInput(data);
                navigate("/:lang/list-my-property/get-started/automatic/lists", {
                    params: {
                        lang,
                    },
                });
            } else {
                sessionStorage.setItem("bcomUrl", data.sanitizedUrl);
                window.location.assign(`/homes/${lang}/onboarding?propertyType=${PropertyType.HOTEL_AND_MORE}`);
            }
        },
        onError: (error: AxiosError<ExtendedScrapingErrorResponse>) => {
            // Ignore manually aborted requests
            if (error.name === "CanceledError") return;

            setError(error.response?.data.errorType ?? scrapingErrorResponseMapper.UnexpectedError);
        },
    });

    const onBack = () => {
        navigate("/:lang/list-my-property/get-started", {
            params: { lang },
        });
    };

    const onNext = () => {
        mutate(input);
    };

    const handleSetInput = (value: string) => {
        if (error !== null) setError(null);
        setInput(value);
    };

    return {
        input,
        setInput: handleSetInput,
        isLoading,
        error,
        onBack,
        onNext,
    };
};
